import { useState } from "react";
import { Select } from "@shared/components/Select";
import { TextArea } from "@shared/components/TextArea";
import { TextField } from "@shared/components/TextField";
import { LIMITS, isHttpsUrl, type ArticleFormValues, type FormErrors } from "./formModel";
import type { ArticleKind, AssessmentOption } from "./types";

interface ArticleFormProps {
  values: ArticleFormValues;
  errors: FormErrors;
  assessments: AssessmentOption[];
  onChange: (patch: Partial<ArticleFormValues>) => void;
}

const KIND_OPTIONS = [
  { value: "article", label: "Artikel" },
  { value: "tip", label: "Tip" },
];

function CoverPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  const clean = url.trim();
  if (!clean || !isHttpsUrl(clean)) return null;
  if (failed === clean) return <p className="field-hint">Gambar tidak dapat dimuat dari URL ini.</p>;
  return (
    <img
      src={clean}
      alt="Pratinjau sampul"
      referrerPolicy="no-referrer"
      className="cover-preview"
      onError={() => setFailed(clean)}
    />
  );
}

export function ArticleForm({ values, errors, assessments, onChange }: ArticleFormProps) {
  const left = LIMITS.summary - values.summary.length;
  const options = [{ value: "", label: "Tidak ada" }, ...assessments.map((a) => ({ value: a.code, label: a.name }))];
  // Keep a saved code selectable even if the list has not loaded (or no longer contains it).
  if (values.relatedAssessmentCode && !options.some((o) => o.value === values.relatedAssessmentCode)) {
    options.push({ value: values.relatedAssessmentCode, label: values.relatedAssessmentCode });
  }

  return (
    <div className="form-grid">
      <TextField label="Judul" value={values.title} error={errors.title} onChange={(e) => onChange({ title: e.target.value })} />
      <TextArea
        label="Ringkasan"
        rows={3}
        value={values.summary}
        error={errors.summary}
        hint={left >= 0 ? `Sisa ${left} karakter` : undefined}
        onChange={(e) => onChange({ summary: e.target.value })}
      />
      <Select label="Jenis" options={KIND_OPTIONS} value={values.kind} error={errors.kind} onChange={(e) => onChange({ kind: e.target.value as ArticleKind })} />
      <TextField
        label="Tag"
        value={values.tags}
        error={errors.tags}
        hint="Pisahkan dengan koma, maksimal 5 tag."
        onChange={(e) => onChange({ tags: e.target.value })}
      />
      <Select
        label="Tes terkait"
        options={options}
        error={errors.relatedAssessmentCode}
        value={values.relatedAssessmentCode}
        onChange={(e) => onChange({ relatedAssessmentCode: e.target.value })}
      />
      <TextField
        label="URL gambar sampul"
        value={values.coverImageUrl}
        error={errors.coverImageUrl}
        hint="Opsional. Alamat gambar harus diawali https://"
        onChange={(e) => onChange({ coverImageUrl: e.target.value })}
      />
      <CoverPreview url={values.coverImageUrl} />
      <TextArea
        label="Isi artikel"
        className="mono"
        rows={18}
        value={values.bodySource}
        error={errors.bodySource}
        hint="## Subjudul, ### Sub-subjudul, - butir, 1. langkah, > kutipan, ![teks](https://…) gambar. Baris kosong memisahkan paragraf."
        onChange={(e) => onChange({ bodySource: e.target.value })}
      />
      <label className="check">
        <input type="checkbox" checked={values.isPlaceholder} onChange={(e) => onChange({ isPlaceholder: e.target.checked })} />
        Tandai sebagai placeholder
      </label>
    </div>
  );
}
